(() => {
  'use strict';

  const nutPdpAutoBundleSelector = '[data-nut-pdp-auto-bundle]';
  const nutPdpAutoBundleApiVersion = '2026-07';

  const nutPdpAutoBundleReplaceTokens = (template, values = {}) => {
    let output = String(template || '');

    Object.entries(values).forEach(([key, value]) => {
      output = output.split(`[${key}]`).join(String(value));
    });

    return output;
  };

  const nutPdpAutoBundleMoney = (amount, currency, locale) => {
    const value = Number(amount || 0);

    try {
      const integer = Number.isInteger(value);

      return new Intl.NumberFormat(locale || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: integer ? 0 : 2,
        maximumFractionDigits: 2
      }).format(value);
    } catch (_error) {
      return `${currency} ${value.toFixed(2)}`;
    }
  };

  const nutPdpAutoBundleLanguage = (locale) => {
    const language = String(locale || '')
      .split('-')[0]
      .toLowerCase();

    if (language === 'ar') return 'AR';
    if (language === 'en') return 'EN';

    return '';
  };

  const nutPdpAutoBundleGid = (variantId) => {
    const raw = String(variantId || '').trim();

    if (!raw) return '';

    if (raw.startsWith('gid://shopify/ProductVariant/')) {
      return raw;
    }

    if (!/^\d+$/.test(raw)) {
      return '';
    }

    return `gid://shopify/ProductVariant/${raw}`;
  };

  const nutPdpAutoBundleCurrentVariantId = (root) => {
    const scope =
      root.closest('.nut-pdp-section') ||
      document;

    const inputs = Array.from(
      scope.querySelectorAll(
        'form[action*="/cart/add"] [name="id"], [name="id"][form]'
      )
    );

    const current = inputs.find((input) => {
      return /^\d+$/.test(String(input.value || ''));
    });

    return (
      current?.value ||
      root.dataset.nutPdpVariantId ||
      ''
    );
  };

  const nutPdpAutoBundleQuery = (locale) => {
    const language =
      nutPdpAutoBundleLanguage(locale);

    const context =
      language
        ? ` @inContext(language: ${language})`
        : '';

    return `
      query NutPdpAutoBundle($id: ID!)${context} {
        node(id: $id) {
          ... on ProductVariant {
            id
            title
            requiresComponents
            price {
              amount
              currencyCode
            }
            components(first: 30) {
              nodes {
                quantity
                productVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  image {
                    url
                    altText
                    width
                    height
                  }
                  product {
                    title
                    handle
                  }
                }
              }
            }
          }
        }
      }
    `;
  };

  const nutPdpAutoBundleFetch = async (variantId, locale) => {
    const gid =
      nutPdpAutoBundleGid(variantId);

    if (!gid) {
      return null;
    }

    const response = await fetch(
      `/api/${nutPdpAutoBundleApiVersion}/graphql.json`,
      {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          query:
            nutPdpAutoBundleQuery(locale),
          variables: {
            id: gid
          }
        })
      }
    );

    const payload = await response.json();

    if (
      !response.ok ||
      payload.errors?.length
    ) {
      throw new Error(
        payload.errors?.[0]?.message ||
        'Bundle query failed'
      );
    }

    return payload?.data?.node || null;
  };

  const nutPdpAutoBundleClear = (root) => {
    root.hidden = true;

    const items =
      root.querySelector(
        '[data-nut-pdp-auto-bundle-items]'
      );

    const badges =
      root.querySelector(
        '[data-nut-pdp-auto-bundle-badges]'
      );

    if (items) items.innerHTML = '';
    if (badges) badges.innerHTML = '';
  };

  const nutPdpAutoBundleCreateBadge = (text, primary = false) => {
    const badge =
      document.createElement('span');

    badge.className =
      `nut-pdp-auto-bundle__badge${primary ? ' nut-pdp-auto-bundle__badge--primary' : ''}`;

    badge.textContent = text;

    return badge;
  };

  const nutPdpAutoBundleCreateItem = (
    component,
    locale
  ) => {
    const variant =
      component?.productVariant;

    if (!variant) {
      return null;
    }

    const quantity =
      Math.max(
        1,
        Number(component.quantity || 1)
      );

    const amount =
      Number(variant.price?.amount || 0);

    const currency =
      variant.price?.currencyCode || 'EGP';

    const lineTotal =
      amount * quantity;

    const item =
      document.createElement('div');

    item.className =
      'nut-pdp-auto-bundle__item';

    const media =
      document.createElement('div');

    media.className =
      'nut-pdp-auto-bundle__media';

    if (variant.image?.url) {
      const image =
        document.createElement('img');

      image.className =
        'nut-pdp-auto-bundle__image';

      image.src =
        variant.image.url;

      image.alt =
        variant.image.altText ||
        variant.product?.title ||
        '';

      image.loading =
        'lazy';

      media.appendChild(image);
    }

    const copy =
      document.createElement('div');

    copy.className =
      'nut-pdp-auto-bundle__copy';

    const title =
      document.createElement('strong');

    title.className =
      'nut-pdp-auto-bundle__title';

    title.textContent =
      variant.product?.title ||
      '';

    copy.appendChild(title);

    const meta =
      document.createElement('div');

    meta.className =
      'nut-pdp-auto-bundle__meta';

    if (
      variant.title &&
      variant.title !== 'Default Title'
    ) {
      const variantTitle =
        document.createElement('span');

      variantTitle.textContent =
        variant.title;

      meta.appendChild(variantTitle);
    }

    if (quantity > 1) {
      const quantityText =
        document.createElement('span');

      quantityText.textContent =
        `× ${quantity}`;

      meta.appendChild(quantityText);
    }

    if (meta.childNodes.length) {
      copy.appendChild(meta);
    }

    const price =
      document.createElement('div');

    price.className =
      'nut-pdp-auto-bundle__item-price';

    const strike =
      document.createElement('s');

    strike.textContent =
      nutPdpAutoBundleMoney(
        lineTotal,
        currency,
        locale
      );

    price.appendChild(strike);

    item.append(
      media,
      copy,
      price
    );

    return item;
  };

  const nutPdpAutoBundleRender = (
    root,
    variant
  ) => {
    const components =
      variant?.components?.nodes || [];

    if (
      !variant?.requiresComponents ||
      !components.length
    ) {
      nutPdpAutoBundleClear(root);
      return;
    }

    const locale =
      root.dataset.nutPdpLocale ||
      document.documentElement.lang ||
      'en';

    const currency =
      variant.price?.currencyCode ||
      components[0]?.productVariant?.price?.currencyCode ||
      'EGP';

    const bundlePrice =
      Number(
        variant.price?.amount || 0
      );

    const separateTotal =
      components.reduce(
        (total, component) => {
          const price =
            Number(
              component?.productVariant?.price?.amount ||
              0
            );

          const quantity =
            Math.max(
              1,
              Number(component?.quantity || 1)
            );

          return total + (price * quantity);
        },
        0
      );

    const saving =
      Math.max(
        0,
        separateTotal - bundlePrice
      );

    const discount =
      separateTotal > 0 && saving > 0
        ? Math.round(
            (saving / separateTotal) * 100
          )
        : 0;

    const badges =
      root.querySelector(
        '[data-nut-pdp-auto-bundle-badges]'
      );

    const items =
      root.querySelector(
        '[data-nut-pdp-auto-bundle-items]'
      );

    const save =
      root.querySelector(
        '[data-nut-pdp-auto-bundle-save]'
      );

    const separate =
      root.querySelector(
        '[data-nut-pdp-auto-bundle-separate]'
      );

    const price =
      root.querySelector(
        '[data-nut-pdp-auto-bundle-price]'
      );

    if (badges) {
      badges.innerHTML = '';

      const countLabel =
        nutPdpAutoBundleReplaceTokens(
          root.dataset.nutPdpLabelProductCount,
          {
            count: components.length
          }
        );

      badges.appendChild(
        nutPdpAutoBundleCreateBadge(
          countLabel,
          true
        )
      );

      if (discount > 0) {
        badges.appendChild(
          nutPdpAutoBundleCreateBadge(
            `-${discount}% ${root.dataset.nutPdpLabelBundled}`
          )
        );
      }
    }

    if (items) {
      items.innerHTML = '';

      components.forEach(
        (component) => {
          const item =
            nutPdpAutoBundleCreateItem(
              component,
              locale
            );

          if (item) {
            items.appendChild(item);
          }
        }
      );
    }

    if (save) {
      if (saving > 0) {
        save.hidden = false;

        save.textContent =
          `${root.dataset.nutPdpLabelSave} ${nutPdpAutoBundleMoney(
            saving,
            currency,
            locale
          )}`;
      } else {
        save.hidden = true;
        save.textContent = '';
      }
    }

    if (separate) {
      separate.textContent =
        nutPdpAutoBundleMoney(
          separateTotal,
          currency,
          locale
        );
    }

    if (price) {
      price.textContent =
        nutPdpAutoBundleMoney(
          bundlePrice,
          currency,
          locale
        );
    }

    root.hidden = false;
  };

  const nutPdpAutoBundleLoad = async (root) => {
    const variantId =
      nutPdpAutoBundleCurrentVariantId(root);

    if (!variantId) {
      nutPdpAutoBundleClear(root);
      return;
    }

    if (
      root.dataset.nutPdpLoadedVariantId ===
      String(variantId)
    ) {
      return;
    }

    root.dataset.nutPdpLoadedVariantId =
      String(variantId);

    try {
      const variant =
        await nutPdpAutoBundleFetch(
          variantId,
          root.dataset.nutPdpLocale
        );

      nutPdpAutoBundleRender(
        root,
        variant
      );
    } catch (error) {
      console.warn(
        '[NUT PDP Auto Bundle]',
        error
      );

      delete root.dataset.nutPdpLoadedVariantId;

      nutPdpAutoBundleClear(root);
    }
  };

  const nutPdpAutoBundleInitRoot = (root) => {
    if (
      root.dataset.nutPdpAutoBundleInitialized ===
      'true'
    ) {
      return;
    }

    root.dataset.nutPdpAutoBundleInitialized =
      'true';

    let timer = null;

    const schedule = () => {
      window.clearTimeout(timer);

      timer =
        window.setTimeout(
          () => {
            delete root.dataset.nutPdpLoadedVariantId;
            nutPdpAutoBundleLoad(root);
          },
          80
        );
    };

    document.addEventListener(
      'change',
      (event) => {
        const target =
          event.target;

        if (
          target instanceof HTMLElement &&
          (
            target.matches('[name="id"]') ||
            target.closest(
              '.nut-pdp-section [data-option-value], .nut-pdp-section [data-variant-picker]'
            )
          )
        ) {
          schedule();
        }
      },
      true
    );

    [
      'variant:change',
      'product:variant-change'
    ].forEach((eventName) => {
      document.addEventListener(
        eventName,
        schedule
      );
    });

    nutPdpAutoBundleLoad(root);
  };

  const nutPdpAutoBundleInit = () => {
    document
      .querySelectorAll(
        nutPdpAutoBundleSelector
      )
      .forEach(
        nutPdpAutoBundleInitRoot
      );
  };

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      nutPdpAutoBundleInit,
      {
        once: true
      }
    );
  } else {
    nutPdpAutoBundleInit();
  }

  document.addEventListener(
    'shopify:section:load',
    nutPdpAutoBundleInit
  );
})();